import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CreditCard, CheckCircle2, AlertCircle, Percent, ExternalLink } from "lucide-react";

export default function PaymentsSettings({ resto }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/owner/stripe/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false }));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/owner/stripe/connect", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e), { duration: 8000 }); setLoading(false); }
  };

  const rate = Math.round((resto?.commission_rate || 0.07) * 100);
  const ready = status?.charges_enabled;

  return (
    <div className="max-w-2xl space-y-5">
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#635bff]/10 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-6 h-6 text-[#635bff]" />
          </div>
          <div className="flex-1">
            <h3 className="font-admin font-bold text-lg">Stripe Connect</h3>
            <p className="text-sm text-neutral-500 mt-1">
              Conecta tu cuenta para recibir automáticamente el neto de cada venta. La plataforma retiene el {rate}% de comisión por transacción.
            </p>
            <div className="mt-4">
              {status === null ? (
                <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
              ) : ready ? (
                <div className="flex items-center gap-2 text-accent-foreground bg-accent/10 rounded-lg px-3 py-2 text-sm w-fit" data-testid="stripe-connected">
                  <CheckCircle2 className="w-4 h-4" /> Cuenta conectada · transferencias automáticas activas
                </div>
              ) : (
                <div className="space-y-3">
                  {status.connected && (
                    <div className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg px-3 py-2 text-sm w-fit">
                      <AlertCircle className="w-4 h-4" /> Onboarding pendiente
                    </div>
                  )}
                  <Button onClick={connect} disabled={loading} data-testid="stripe-connect-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-1" /> Conectar con Stripe</>}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <Percent className="w-5 h-5 text-primary" />
          <h3 className="font-admin font-bold">Cómo funciona la división de pagos</h3>
        </div>
        <ul className="space-y-2 text-sm text-neutral-600">
          <li>• El cliente paga el total del pedido con tarjeta (Stripe Checkout).</li>
          <li>• Se calcula y registra automáticamente la comisión del <b>{rate}%</b> en cada pedido.</li>
          <li>• Con Connect activo, el neto se transfiere a tu cuenta y la comisión queda en la plataforma (destination charge).</li>
          <li>• Mientras Connect no esté habilitado, la plataforma cobra y contabiliza tu neto para liquidación.</li>
        </ul>
        <p className="text-xs text-neutral-400 mt-4">Modo test · usa la tarjeta 4242 4242 4242 4242, fecha futura y cualquier CVC.</p>
      </div>
    </div>
  );
}
